import { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { FILTER_ON_GROUP, FILTER } from "helpers/shapes"
import AddFilterNew from "components/add-filter"
import NormalizationRadioGroup from "components/item-radio-group"
import { NORMALIZATION } from "helpers/segments"
import { SECURITY_IDS } from "components/add-filter/shapes"

export default function AnalyzeHeaderFilters({
  normalization,
  setNormalization,
  filterOns,
  account,
  modelId,
  assetId,
  assetType,
  startDate,
  endDate,
  heapAddFilterId,
  disableNormalization,
  disableNormalizationTooltip,
  intraday,
  isExpanded,
  hasUnsupportedContributionFilter,
  currency,
  securityIds,
  filtersVisible,
  toggleFilters,
  anchorElement,
  addedFilters,
  updateFilters,
}) {
  const [internalNormalization, setInternalNormalization] = useState(normalization)

  const handleUpdateFilters = useCallback((filters) => {
    updateFilters(filters)
    setNormalization(internalNormalization)
  }, [updateFilters, setNormalization, internalNormalization])

  const handleSetNormalization = (normalization) => {
    setInternalNormalization(normalization)
  }

  const normalizationOptions = [
    {
      id: NORMALIZATION.CONTRIBUTION,
      primaryText: "Contribution",
      secondaryText: <>Constituents&apos; weights are calculated by their respective contribution to the non-filtered portfolio. <i>Default</i></>,
      disabled: hasUnsupportedContributionFilter,
      tooltip: hasUnsupportedContributionFilter ? "Filtering on position tags or attributes is not allowed in contribution mode when active." : null,
    },
    {
      id: NORMALIZATION.STANDALONE,
      primaryText: "Standalone",
      secondaryText: "The weights of the segment constituents are calculated as if it were a standalone portfolio",
    },
  ]

  return (
    <AddFilterNew
      filterOns={filterOns}
      updateFilters={handleUpdateFilters}
      account={account}
      modelId={modelId}
      assetId={assetId}
      assetType={assetType}
      startDate={startDate}
      endDate={endDate}
      heapAddFilterId={heapAddFilterId}
      intraday={intraday}
      isExpanded={isExpanded}
      currency={currency}
      securityIds={securityIds}
      anchorEl={anchorElement}
      filtersVisible={filtersVisible}
      toggleFilters={toggleFilters}
      existingFilters={addedFilters ?? []}
      additionalFilterComponent={(
        <NormalizationRadioGroup
          disabled={disableNormalization}
          disableItemTooltip={true}
          disabledTooltip={disableNormalizationTooltip}
          label="Normalization"
          value={internalNormalization}
          setValue={handleSetNormalization}
          options={normalizationOptions}
          classes={{ label: "filter-header" }}
        />
      )}
      showAdditionalFilterComponentOnActiveFilterOnly={true}
    />
  )
}

AnalyzeHeaderFilters.propTypes = {
  setNormalization: PropTypes.func.isRequired,
  normalization: PropTypes.oneOf(map(NORMALIZATION)),
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  assetId: PropTypes.string,
  assetType: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  heapAddFilterId: PropTypes.string.isRequired,
  disableNormalization: PropTypes.bool,
  disableNormalizationTooltip: PropTypes.string,
  intraday: PropTypes.bool,
  isExpanded: PropTypes.bool,
  hasUnsupportedContributionFilter: PropTypes.bool,
  currency: PropTypes.string,
  securityIds: SECURITY_IDS,
  filtersVisible: PropTypes.bool,
  toggleFilters: PropTypes.func,
  anchorElement: PropTypes.object,
  updateFilters: PropTypes.func,
  addedFilters: PropTypes.arrayOf(FILTER),
}
